import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { KycApiService } from '../core/kyc-api.service';
import { KycMetrics, KycMetricsHistoryItem, KycStatus } from '../core/models';

type DashboardMetric = {
  label: string;
  value: number;
  tone: 'neutral' | 'positive' | 'warning' | 'danger';
  caption: string;
};

type StatusCard = {
  label: string;
  key: keyof KycMetrics['byStatus'];
  tone: 'neutral' | 'positive' | 'warning' | 'danger';
  value: number;
};

const STATUS_CARD_DEFS: Array<Omit<StatusCard, 'value'>> = [
  { label: 'Enviados', key: 'sent', tone: 'neutral' },
  { label: 'En progreso', key: 'inProgress', tone: 'warning' },
  { label: 'Completados', key: 'completed', tone: 'neutral' },
  { label: 'En revisión', key: 'inReview', tone: 'warning' },
  { label: 'Con correcciones', key: 'changesRequested', tone: 'warning' },
  { label: 'Aprobados', key: 'approved', tone: 'positive' },
  { label: 'Denegados', key: 'denied', tone: 'danger' },
];

@Component({
  selector: 'app-kyc-dashboard',
  imports: [DatePipe],
  templateUrl: './kyc-dashboard.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KycDashboardComponent {
  private readonly kycApi = inject(KycApiService);

  readonly loading = signal(true);
  readonly metrics = signal<KycMetrics | null>(null);
  readonly history = signal<KycMetricsHistoryItem[]>([]);
  readonly verificationRate = computed(() => {
    const current = this.metrics();
    if (!current || current.total === 0) {
      return 0;
    }

    return Math.round((current.verified / current.total) * 100);
  });
  readonly metricsCards = computed<DashboardMetric[]>(() => {
    const current = this.metrics();
    if (!current) {
      return [];
    }

    return [
      {
        label: 'Casos totales',
        value: current.total,
        tone: 'neutral',
        caption: 'Volumen total de procesos KYC registrados.',
      },
      {
        label: 'Aprobados',
        value: current.verified,
        tone: 'positive',
        caption: `${this.verificationRate()}% del total fue aprobado.`,
      },
      {
        label: 'Pendientes',
        value: current.pending,
        tone: 'warning',
        caption: 'Casos aún no resueltos en algún estado intermedio.',
      },
      {
        label: 'Denegados',
        value: current.failed,
        tone: 'danger',
        caption: 'Casos detenidos por validación o riesgo.',
      },
    ];
  });

  readonly statusCards = computed<StatusCard[]>(() => {
    const current = this.metrics();
    if (!current) {
      return [];
    }
    return STATUS_CARD_DEFS.map((def) => ({
      ...def,
      value: current.byStatus[def.key],
    }));
  });
  readonly spotlight = computed(() => {
    const current = this.metrics();
    if (!current) {
      return null;
    }

    return {
      pendingFocus:
        current.pending > 0
          ? 'Atienda la cola pendiente para evitar cuellos de botella.'
          : 'No hay revisiones pendientes ahora mismo.',
      failureFocus:
        current.failed > 0
          ? 'Revise los casos denegados para detectar patrones de riesgo.'
          : 'No hay denegaciones recientes reportadas.',
    };
  });
  readonly latestEntry = computed(() => this.history()[0] ?? null);

  constructor() {
    this.load();
  }

  statusLabel(status: KycStatus | string): string {
    switch (status) {
      case 'sent':
        return 'Enviado';
      case 'in_progress':
        return 'En progreso';
      case 'completed':
        return 'Completado';
      case 'in_review':
        return 'En revisión';
      case 'changes_requested':
        return 'Con correcciones';
      case 'approved':
        return 'Aprobado';
      case 'denied':
        return 'Denegado';
      default:
        return status as string;
    }
  }

  statusTone(status: KycStatus | string): 'positive' | 'warning' | 'danger' | 'neutral' {
    switch (status) {
      case 'approved':
        return 'positive';
      case 'denied':
        return 'danger';
      case 'in_progress':
      case 'in_review':
      case 'changes_requested':
        return 'warning';
      default:
        return 'neutral';
    }
  }

  async load() {
    this.loading.set(true);
    try {
      const data = await firstValueFrom(this.kycApi.getMetrics());
      this.metrics.set(data);
      this.history.set(data.history ?? []);
    } catch {
      this.metrics.set(null);
      this.history.set([]);
    } finally {
      this.loading.set(false);
    }
  }
}
